import { NavigationActions, NavigationApiActions } from '@common/store/navigation/actions/navigation.actions';
import {
    Navigation,
    NavigationGroup,
    NavigationGroupTypes,
    NavigationItem,
    NavigationItemTypes,
    NavigationSection,
    NavigationSectionTypes,
    NavigationState,
} from '@common/store/navigation/models/navigation.models';
import { createReducer, on } from '@ngrx/store';
import { APPLICATIONS, AppName, ViewStateTypeNames } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { Util } from '@src/suite/common/utils/util';
import { isSearchViewStateRecord } from '@src/suite/common/utils/viewstate-utils';

import { ApplicationActions } from '../actions/application.actions';

export const initialState: NavigationState = {
    raw: null,
    loaded: false,
    application: AppName.PLAYBOOK,
    urlToNavigate: null,
    groupToNavigate: null,
    sectionToNavigate: null,
    itemToNavigate: null,
    componentToNavigate: null,
    redirectAfterLogin: null,
    group: null,
    section: null,
    item: null,
    team: null,
    navigation: {
        cast: new Array<NavigationGroup>(),
        discover: new Array<NavigationGroup>(),
        playbook: new Array<NavigationGroup>(),
        live: new Array<NavigationGroup>(),
        wastebin: new Array<NavigationGroup>(),
        templates: new Array<NavigationGroup>(),
        embedded: new Array<NavigationGroup>(),
    },
};

export const findDefaultNavigationItem = (groups: NavigationGroup[]): { group: string; section: string; item: string; url: string[] } => {
    let groupToNavigate: string = null;
    let sectionToNavigate: string = null;
    let itemToNavigate: string = null;
    let urlToNavigate: string[] = null;

    const defaultGuid = groups?.findLast((group: NavigationGroup) => !!group.DefaultGuid)?.DefaultGuid;

    groups?.forEach((group: NavigationGroup) => {
        if (group.ID === NavigationGroupTypes.System) return;
        group?.Items?.forEach((section: NavigationSection) => {
            if (section.ID === NavigationSectionTypes.System) return;
            section?.Items?.forEach((item: NavigationItem) => {
                if (!itemToNavigate) {
                    groupToNavigate = group.Path;
                    sectionToNavigate = section.Path;
                    itemToNavigate = item.Guid;
                    urlToNavigate = item.Url;
                }

                if (defaultGuid === item.ViewState?.Guid) {
                    groupToNavigate = group.Path;
                    sectionToNavigate = section.Path;
                    itemToNavigate = item.Guid;
                    urlToNavigate = item.Url;
                }
            });
        });
    });

    return {
        group: groupToNavigate,
        section: sectionToNavigate,
        item: itemToNavigate,
        url: urlToNavigate,
    };
};

export const handleAddNavigationItem = (state: NavigationState, grp: NavigationGroup, sec: NavigationSection, itm: NavigationItem): NavigationState => {
    let group: NavigationGroup = null;
    let section: NavigationSection = null;
    let item: NavigationItem = null;
    //state = { ...state, navigation: { ...state.navigation } };
    const navigation = [...state.navigation[state.application]].map((g: NavigationGroup) => {
        if (g.ID === grp.ID) {
            group = { ...g };
            return {
                ...group,
                Items: [
                    ...group.Items.map((s: NavigationSection) => {
                        if (s.ID === sec.ID) {
                            section = { ...s };
                            if (section.ID === NavigationSectionTypes.System) {
                                section.Items = section.Items.map(i => {
                                    if (i.Guid === itm.Guid) {
                                        item = { ...itm };
                                        return item;
                                    }
                                    return i;
                                });
                            } else {
                                item = { ...itm };
                                item.Order = section.Items.length;
                                section.Items = [...section.Items, item];
                            }
                            return section;
                        } else {
                            return s;
                        }
                    }),
                ],
            };
        } else {
            return g;
        }
    });

    if (group && section && item) {
        return { ...state, navigation: { ...state.navigation, [state.application]: navigation }, group: { ...group }, section: { ...section }, item: { ...item } };
    }
    return { ...state, navigation: { ...state.navigation, [state.application]: navigation } };
};

export const handleSetNavigationItem = (state: NavigationState, grp: string, sec: string, itm: string): NavigationState => {
    let group: NavigationGroup = null;
    let section: NavigationSection = null;
    let item: NavigationItem = null;
    const newNavigationState = { ...state.navigation };

    if (grp && sec && itm) {
        newNavigationState[state.application] = newNavigationState[state.application]?.map((g: NavigationGroup) => {
            if (g.Path === grp) {
                group = {
                    ...g,
                    Opened: true,
                    Items: g.Items?.map((s: NavigationSection) => {
                        if ((g.Path !== 'system' && s.Path === sec) || (g.Path === 'system' && s.Path === 'system')) {
                            section = s;
                            item = s.Items.find((i: NavigationItem) => i.Guid === itm);
                            return { ...s, Opened: true };
                        } else {
                            return s;
                        }
                    }),
                };
                return group;
            } else {
                return g;
            }
        });
        if (item && item !== state.item) {
            if (item.ViewState) {
                const newViewState = { ...item.ViewState };
                return { ...state, group: { ...group }, section: { ...section }, item: { ...item, ViewState: newViewState } };
            }
            return state;
        } else {
            return state;
        }
    } else {
        return { ...state, group: null, section: null, item: null };
    }
};

export const handleUpdateSingleItemNavigationItemViewState = (item: NavigationItem, guid?: string, itemtype?: ObjectTypes, title?: string): NavigationItem => {
    const vs = { ...item.ViewState } as StorytellingSingleItemViewStateRecord;
    if (guid) {
        item = { ...item, Guid: guid };
        vs.Title = 'Loading...';
        vs.itemGuid = guid;
    }
    if (itemtype) {
        vs.itemType = itemtype;
    }
    if (title) {
        vs.Title = title;
    }
    return { ...item, Label: vs.Title, ViewState: vs };
};

export const handleUpdateSingleNavigationItem = (state: NavigationState, guid?: string, itemtype?: ObjectTypes, title?: string): NavigationState => {
    const item: NavigationItem = null;

    const navigation = {
        ...state.navigation,
        [state.application]: state.navigation[state.application].map((g: NavigationGroup) => {
            if (g.ID === NavigationGroupTypes.System) {
                return {
                    ...g,
                    Items: g.Items.map((s: NavigationSection) => {
                        if (s.ID === NavigationSectionTypes.System) {
                            return {
                                ...s,
                                Items: s.Items.map((i: NavigationItem) => {
                                    if (i?.ViewState?.PSTViewStateType === ViewStateTypes.SINGLEITEM) {
                                        return handleUpdateSingleItemNavigationItemViewState(i, guid, itemtype, title);
                                    } else {
                                        return i;
                                    }
                                }),
                            };
                        } else {
                            return s;
                        }
                    }),
                };
            } else {
                return g;
            }
        }),
    };

    return { ...state, item: item, navigation: navigation };
};

export const findNavigationItem = (groups: NavigationGroup[], group: string, section: string, guid: string): NavigationItem => {
    let item: NavigationItem;
    exit: for (const grp of groups) {
        if (grp.Path === group) {
            if (grp.ID === NavigationGroupTypes.System) {
                item = grp.Items[0].Items?.find((itm: NavigationItem) => itm.Guid === guid);
                break exit;
            } else {
                for (const sec of grp.Items) {
                    if (sec.Path === section) {
                        item = sec.Items.find((itm: NavigationItem) => itm.Guid === guid);
                        break exit;
                    }
                }
            }
        }
    }
    return item;
};

export const handleModifySection = (state: NavigationState, group: NavigationGroup, section: NavigationSection, handler: (section: NavigationSection) => NavigationSection): NavigationState => {
    return {
        ...state,
        navigation: {
            ...state.navigation,
            [state.application]: [
                ...state.navigation[state.application].map((grp: NavigationGroup) => {
                    if (grp.ID === group.ID) {
                        return {
                            ...grp,
                            Items: grp.Items.map((sec: NavigationSection) => {
                                if (sec.ID === section.ID) {
                                    return handler(section);
                                } else {
                                    return sec;
                                }
                            }),
                        };
                    } else {
                        return grp;
                    }
                }),
            ],
        },
    };
};

export const handleRenameSection = (section: NavigationSection): NavigationSection => {
    return { ...section, PrevOpened: section.Opened, Opened: true, PrevLabel: section.Label, InEditMode: true };
};

export const handleCancelSection = (section: NavigationSection): NavigationSection => {
    return { ...section, InEditMode: false, Opened: section.PrevOpened, Label: section.PrevLabel };
};

export const handleUpdateSection = (section: NavigationSection): NavigationSection => {
    return { ...section };
};

export const handleUpdateSectionState = (section: NavigationSection): NavigationSection => {
    return { ...section, Opened: !section.Opened };
};

export const navigationReducer = createReducer(
    initialState,
    on(ApplicationActions.sessionDestroyed, (): NavigationState => {
        return initialState;
    }),
    on(NavigationActions.loadNavigation, (state, action): NavigationState => {
        return {
            ...state,
            raw: action.navigation,
        };
    }),
    on(NavigationActions.initializeNavigationSuccess, (state, action): NavigationState => {
        const fullNavigation = APPLICATIONS.reduce((acc, application: AppName) => {
            let navigation = [...action.navigation[application]];
            navigation = navigation.toSorted((a: NavigationGroup, b: NavigationGroup) => {
                if (a.ID < b.ID) return -1;
                if (a.ID > b.ID) return 1;
                return 0;
            });
            navigation = navigation.map((group: NavigationGroup) => {
                return {
                    ...group,
                    Items: group.Items.toSorted((a: NavigationSection, b: NavigationSection) => {
                        if (a.ID < b.ID) return -1;
                        if (a.ID > b.ID) return 1;
                        return 0;
                    }),
                };
            });
            acc[application] = navigation;
            return acc;
        }, {} as Navigation);

        let groupToNavigate: string = null;
        let sectionToNavigate: string = null;
        let itemToNavigate: string = null;
        let urlToNavigate: string[] = [];
        if (state.application) {
            const requestedItem =
                state.groupToNavigate && state.sectionToNavigate && state.itemToNavigate
                    ? findNavigationItem(fullNavigation[state.application], state.groupToNavigate, state.sectionToNavigate, state.itemToNavigate)
                    : null;

            if (requestedItem) {
                groupToNavigate = state.groupToNavigate;
                sectionToNavigate = state.sectionToNavigate;
                itemToNavigate = state.itemToNavigate;
                urlToNavigate = requestedItem.Url || [];
            } else {
                // Item not found (e.g. another user's personal view) or no item requested, fall back to default
                const { group, section, item, url } = findDefaultNavigationItem(fullNavigation[state.application]);
                groupToNavigate = group;
                sectionToNavigate = section;
                itemToNavigate = item;
                urlToNavigate = url;
            }
        }
        return {
            ...state,
            loaded: true,
            urlToNavigate: urlToNavigate,
            groupToNavigate: groupToNavigate,
            sectionToNavigate: sectionToNavigate,
            itemToNavigate: itemToNavigate,
            team: action.team,
            navigation: {
                wastebin: action.navigation[AppName.WASTEBIN],
                templates: action.navigation[AppName.TEMPLATES],
                cast: fullNavigation[AppName.CAST],
                discover: fullNavigation[AppName.DISCOVER],
                playbook: fullNavigation[AppName.PLAYBOOK],
                live: fullNavigation[AppName.LIVE],
            },
        };
    }),
    on(NavigationApiActions.loadTeamNavigationData, (state): NavigationState => {
        if (state.application === AppName.WASTEBIN) {
            return state;
        }
        if (state.application === AppName.TEMPLATES) {
            return state;
        }
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: state.navigation[state.application].map((group: NavigationGroup) => {
                    if (group.ID === NavigationGroupTypes.Team) {
                        return { ...group, items: [] };
                    } else {
                        return group;
                    }
                }),
            },
        };
    }),
    on(NavigationApiActions.loadTeamNavigationDataSuccess, (state, action): NavigationState => {
        return {
            ...state,
            team: action.team,
            navigation: {
                ...state.navigation,
                [state.application]: state.navigation[state.application].map((group: NavigationGroup) => {
                    if (group.ID === NavigationGroupTypes.Team) {
                        return { ...group, items: { ...action.navigation } };
                    } else {
                        return group;
                    }
                }),
            },
        };
    }),
    on(NavigationActions.initializeTeamNavigationDataSuccess, (state, action): NavigationState => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: state.navigation[state.application].map((group: NavigationGroup) => {
                    if (group.ID === NavigationGroupTypes.Team) {
                        return { ...group, Items: action.sections };
                    } else {
                        return group;
                    }
                }),
            },
        };
    }),
    on(NavigationActions.setApplication, (state, action): NavigationState => {
        if (action.application === state.application) return state;

        const { group, section, item, url } = findDefaultNavigationItem(state.navigation[action.application]);

        return {
            ...state,
            groupToNavigate: group,
            sectionToNavigate: section,
            itemToNavigate: item,
            urlToNavigate: url,
            application: action.application,
        };
    }),
    on(NavigationActions.navigateToDefaultItem, (state): NavigationState => {
        const { group, section, item, url } = findDefaultNavigationItem(state.navigation[state.application]);
        return {
            ...state,
            groupToNavigate: group,
            sectionToNavigate: section,
            itemToNavigate: item,
            urlToNavigate: url,
            ['application']: state.application,
        };
    }),
    on(NavigationActions.updateGroupState, (state, action): NavigationState => {
        const navigation = {
            ...state.navigation,
            [state.application]: state.navigation[state.application].map((group: NavigationGroup) => {
                if (group.ID === action.group.ID) {
                    return { ...group, Оpened: !group.Opened };
                } else {
                    return group;
                }
            }),
        };
        return {
            ...state,
            navigation,
        };
    }),
    on(NavigationActions.addSection, (state, action): NavigationState => {
        const tmp = action.group.Items.map(section => section.ID);
        const max = tmp && tmp.length ? Math.max(...tmp) : 0;
        const path: string = Util.url(action.label);
        const section = {
            ID: max + 1,
            Label: action.label,
            Order: 10000 + max,
            Hidden: false,
            Enterprise: action.group.ID === NavigationGroupTypes.Global,
            Items: [],
            Opened: true,
            InEditMode: true,
            IsNewSection: true,
            PrevLabel: null,
            PrevOpened: true,
            Path: path,
        };
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map(grp => {
                        if (grp.ID === action.group.ID && grp.Items?.findIndex(item => item.ID === section.ID) === -1) {
                            return { ...grp, Items: [...grp.Items, section] };
                        } else {
                            return grp;
                        }
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.removeSection, (state, action): NavigationState => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        if (group.ID === action.group.ID) {
                            return {
                                ...group,
                                Items: group.Items.filter(section => section.ID !== action.section.ID),
                            };
                        }
                        return group;
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.renameSection, (state, action): NavigationState => {
        return handleModifySection(state, action.group, action.section, handleRenameSection);
    }),
    on(NavigationActions.updateSection, (state, action): NavigationState => {
        return handleModifySection(state, action.group, action.section, handleUpdateSection);
    }),
    on(NavigationActions.cancelSection, (state, action): NavigationState => {
        return handleModifySection(state, action.group, action.section, handleCancelSection);
    }),
    on(NavigationActions.updateSectionState, (state, action): NavigationState => {
        return handleModifySection(state, action.group, action.section, handleUpdateSectionState);
    }),
    on(NavigationActions.addItem, (state, action): NavigationState => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((grp: NavigationGroup) => {
                        return {
                            ...grp,
                            items: [
                                ...grp.Items.map((sec: NavigationSection) => {
                                    if (sec.ID === action.section.ID) {
                                        if (sec.Items.findIndex(itm => itm.Guid === action.item.Guid) > -1) {
                                            return sec;
                                        } else {
                                            return {
                                                ...sec,
                                                items: [...sec.Items, action.item],
                                            };
                                        }
                                    } else {
                                        return sec;
                                    }
                                }),
                            ],
                        };
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.removeItem, (state, action): NavigationState => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        if (group.ID === action.group.ID) {
                            return {
                                ...group,
                                items: group.Items.map((section: NavigationSection) => {
                                    if (section.ID === action.section.ID) {
                                        return {
                                            ...section,
                                            Items: section.Items.filter(item => {
                                                return item.Guid !== action.item.Guid;
                                            }),
                                        };
                                    }
                                    return section;
                                }),
                            };
                        }
                        return group;
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.removeItemFromGroups, (state, action): NavigationState => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        if (action.groups.indexOf(group.ID) > -1) {
                            return {
                                ...group,
                                Items: group.Items.map((section: NavigationSection) => {
                                    return {
                                        ...section,
                                        Items: section.Items.filter(item => {
                                            return item.ViewState.Guid !== action.vs.Guid;
                                        }),
                                    };
                                }),
                            };
                        }
                        return group;
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.setCurrentItem, (state, action): NavigationState => {
        return handleSetNavigationItem(state, action.group, action.section, action.item);
    }),
    on(NavigationActions.navigateToItem, (state, action): NavigationState => {
        if (state.loaded && action.group && action.section && action.item) {
            const item = findNavigationItem(state.navigation[state.application], action.group, action.section, action.item);
            if (!item) {
                // Item not found in navigation (e.g. another user's personal view), fall back to default
                const defaultNav = findDefaultNavigationItem(state.navigation[state.application]);
                return {
                    ...state,
                    groupToNavigate: defaultNav.group,
                    sectionToNavigate: defaultNav.section,
                    itemToNavigate: defaultNav.item,
                    urlToNavigate: defaultNav.url,
                    componentToNavigate: action.component,
                };
            }
        }
        return { ...state, groupToNavigate: action.group, sectionToNavigate: action.section, itemToNavigate: action.item, componentToNavigate: action.component };
    }),
    on(NavigationApiActions.saveCurrentNavigationItemSuccess, (state, _): NavigationState => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        return {
                            ...group,
                            items: group.Items.map((section: NavigationSection) => {
                                return {
                                    ...section,
                                    items: section.Items.map((item: NavigationItem) => {
                                        if (item && item.ViewState && item.ViewState.Guid === state.item.ViewState.Guid) {
                                            return {
                                                ...item,
                                                ViewState: { ...state.item.ViewState },
                                            };
                                        }
                                        return item;
                                    }),
                                };
                            }),
                        };
                    }),
                ],
            },
        };
    }),
    on(NavigationApiActions.updateNavigationItemSuccess, (state, action): NavigationState => {
        let current = state.item;
        if (state.item && state.item.ViewState && state.item.ViewState.Guid === action.vs.Guid) {
            current = {
                ...state.item,
                Label: action.vs.Title,
                ViewState: { ...action.vs },
            };
        }
        return {
            ...state,
            item: current,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        return {
                            ...group,
                            Items: group.Items.map((section: NavigationSection) => {
                                return {
                                    ...section,
                                    Items: section.Items.map((item: NavigationItem) => {
                                        if (item && item.ViewState && item.ViewState.Guid === action.vs.Guid) {
                                            return {
                                                ...item,
                                                Label: action.vs.Title,
                                                ViewState: { ...action.vs },
                                            };
                                        }
                                        return item;
                                    }),
                                };
                            }),
                        };
                    }),
                ],
            },
        };
    }),
    on(NavigationApiActions.createNavigationItemSuccess, (state, action): NavigationState => {
        return handleAddNavigationItem(state, { ...action.group }, { ...action.section }, { ...action.item });
    }),
    on(NavigationApiActions.addNavigationItemSuccess, (state, action): NavigationState => {
        return handleAddNavigationItem(state, action.group, action.section, action.item);
    }),
    on(NavigationActions.enterSearchMode, (state, action): NavigationState => {
        let group: NavigationGroup = null;
        let section: NavigationSection = null;
        let item: NavigationItem = null;

        const updatedNavigation = state.navigation[state.application].map((g: NavigationGroup) => {
            if (g.ID === NavigationGroupTypes.System) {
                group = g;
                const newItems = [
                    ...g.Items.map((s: NavigationSection) => {
                        if (s.ID === NavigationSectionTypes.System) {
                            section = s;
                            const newItems = s.Items.map((it: NavigationItem) => {
                                if (it.Guid === NavigationItemTypes.SYSTEM_SEARCH_GUID) {
                                    item = {
                                        ...it,
                                        ViewState: {
                                            ...it.ViewState,
                                            PSTViewStateSearchQuery: action.term,
                                            Title: `${ViewStateTypeNames.SEARCH} - ${action.searchDisplayValue || action.term}`,
                                        } as StorytellingSearchViewStateRecord,
                                    };

                                    return item;
                                } else {
                                    return it;
                                }
                            });
                            return { ...s, Items: newItems, Opened: true };
                        } else {
                            return s;
                        }
                    }),
                ];
                return { ...g, Items: newItems, opened: true };
            } else {
                return g;
            }
        });
        if (item && item !== state.item && item.ViewState && isSearchViewStateRecord(item.ViewState)) {
            return { ...state, navigation: { ...state.navigation, [state.application]: updatedNavigation }, group: { ...group }, section: { ...section }, item: { ...item } };
        }

        return state;
    }),
    on(NavigationActions.enterWastebinMode, (state): NavigationState => {
        let group: NavigationGroup = null;
        let section: NavigationSection = null;
        let item: NavigationItem = null;

        state.navigation[state.application] = state.navigation[state.application].map((g: NavigationGroup) => {
            if (g.ID === NavigationGroupTypes.System) {
                group = g;
                g.Items = [
                    ...g.Items.map((s: NavigationSection) => {
                        if (s.ID === NavigationSectionTypes.System) {
                            section = s;
                            item = s.Items.find((i: NavigationItem) => i.Guid === NavigationItemTypes.SYSTEM_WASTEBIN_GUID);
                            return { ...s, opened: true };
                        } else {
                            return s;
                        }
                    }),
                ];
                return { ...g, opened: true };
            } else {
                return g;
            }
        });
        if (item && item !== state.item && item.ViewState && item.ViewState.PSTViewStateType === ViewStateTypes.WASTEBIN) {
            item.ViewState.Title = ViewStateTypeNames.WASTEBIN;
            return { ...state, group: { ...group }, section: { ...section }, item: { ...item } };
        }
        if (item && item !== state.item && item.ViewState && item.ViewState.PSTViewStateType === ViewStateTypes.TEMPLATES) {
            item.ViewState.Title = ViewStateTypeNames.TEMPLATES;
            return { ...state, group: { ...group }, section: { ...section }, item: { ...item } };
        }

        return state;
    }),
    on(NavigationActions.loadSingleNavigationItem, (state, action): NavigationState => {
        return handleUpdateSingleNavigationItem(state, action.guid, null);
    }),
    on(NavigationActions.setSingleNavigationItemData, (state, action): NavigationState => {
        return handleUpdateSingleNavigationItem(state, null, action.itemtype, action.title);
    }),
    on(NavigationActions.loadNavigationItem, (state, action): NavigationState => {
        let group: string = null;
        let section: string = null;
        let item: string = null;

        state.navigation[state.application].forEach((g: NavigationGroup) => {
            g.Items.forEach((s: NavigationSection) => {
                s.Items.forEach((i: NavigationItem) => {
                    if (i && i.ViewState && i.ViewState.Guid === action.vs.Guid) {
                        group = g.Path;
                        section = s.Path;
                        if (i.System) {
                            item = i.Guid;
                        } else {
                            item = i.ViewState.Guid;
                        }
                    }
                });
            });
        });

        if (!item) {
            state = {
                ...state,
                navigation: {
                    ...state.navigation,
                    [state.application]: state.navigation[state.application].map((g: NavigationGroup) => {
                        if (g.ID === NavigationGroupTypes.System) {
                            return {
                                ...g,
                                Items: g.Items.map((s: NavigationSection) => {
                                    if (s.ID === NavigationSectionTypes.System) {
                                        return {
                                            ...s,
                                            Items: s.Items.map((i: NavigationItem) => {
                                                if (i.Guid === NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID) {
                                                    group = g.Path;
                                                    section = s.Path;
                                                    item = NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID;
                                                    return { ...i, ViewState: { ...action.vs } };
                                                } else {
                                                    return { ...i };
                                                }
                                            }),
                                        };
                                    } else {
                                        return s;
                                    }
                                }),
                            };
                        } else {
                            return g;
                        }
                    }),
                },
            };
        }
        return handleSetNavigationItem(state, group, section, item);
    }),
    on(NavigationActions.reorderItems, (state, action): NavigationState => {
        const item = findNavigationItem(state.navigation[state.application], action.group.Path, action.sourceSection.Path, action.guid);
        if (item) {
            return {
                ...state,
                navigation: {
                    ...state.navigation,
                    [state.application]: state.navigation[state.application].map((g: NavigationGroup) => {
                        if (g.ID === action.group.ID) {
                            return {
                                ...g,
                                items: g.Items.map((s: NavigationSection) => {
                                    if (action.sourceSection.ID === action.targetSection.ID && action.targetSection.ID === s.ID) {
                                        // We are draggin and dropping within the same section
                                        s.Items.splice(action.sourceIndex, 1);
                                        s.Items.splice(action.targetIndex, 0, item);
                                        return {
                                            ...s,
                                        };
                                    } else {
                                        if (s.ID === action.sourceSection.ID) {
                                            s.Items.splice(action.sourceIndex, 1);
                                            return {
                                                ...s,
                                            };
                                        }
                                        if (s.ID === action.targetSection.ID) {
                                            s.Items.splice(action.targetIndex, 0, item);
                                            return {
                                                ...s,
                                            };
                                        }
                                        return s;
                                    }
                                }),
                            };
                        }
                        return g;
                    }),
                },
            };
        }
        return state;
    }),
    on(NavigationApiActions.setDefaultNavigationItemSuccess, (state, action): NavigationState => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: state.navigation[state.application].map((group: NavigationGroup) => {
                    if (group.ID === action.group) {
                        return {
                            ...group,
                            default: action.guid,
                        };
                    } else {
                        return group;
                    }
                }),
            },
        };
    }),
    on(NavigationActions.setRedirectAfterLogin, (state, action): NavigationState => {
        return { ...state, redirectAfterLogin: action.path };
    }),
    on(NavigationActions.clearRedirectAfterLogin, (state): NavigationState => {
        return { ...state, redirectAfterLogin: null };
    })
);
